/**
 * The Team dock's UI state (SR-14, Antonin's design 1 Nov): open or collapsed, which conversation is open, whether
 * the right side is the "To:" search, and whether the hub is reconnecting. Client UI state only: every message,
 * thread and count lives in TanStack Query.
 */
import { create } from 'zustand';

export type DockConnection = 'connected' | 'reconnecting';

interface DockState {
  open: boolean;
  /** The open conversation (right side), or null for "Choose a conversation…". */
  threadId: string | null;
  /** The right side shows the "To:" search instead of a conversation. */
  composing: boolean;
  connection: DockConnection;
  /**
   * The signed-in user this state (and the staff-chat caches) belongs to. Kept in the store, not in a component,
   * because the shell unmounts on the way to /login: a remount for the next user compares against this.
   */
  ownerUserId: string | null;
  expand: () => void;
  collapse: () => void;
  toggle: () => void;
  /** Opens the dock on one conversation (a dashboard row, a deep link, a picked colleague). */
  openThread: (threadId: string) => void;
  /** Opens the dock on the "To:" search. */
  openNewMessage: () => void;
  setConnection: (connection: DockConnection) => void;
}

export const useStaffChatDock = create<DockState>((set) => ({
  open: false,
  threadId: null,
  composing: false,
  connection: 'connected',
  ownerUserId: null,
  expand: () => set({ open: true }),
  collapse: () => set({ open: false }),
  toggle: () => set((s) => ({ open: !s.open })),
  openThread: (threadId) => set({ open: true, threadId, composing: false }),
  openNewMessage: () => set({ open: true, composing: true }),
  setConnection: (connection) => set({ connection }),
}));

/** Back to the first state: on sign-out or a change of user (`useStaffChatShell`), and in tests and stories. */
export function resetStaffChatDock(): void {
  useStaffChatDock.setState({ open: false, threadId: null, composing: false, connection: 'connected' });
}
